import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { MONTHS, CAL_CATS } from "../../content/calendar";
import { SectionHead } from "./SectionHead";
import { Reveal, EASE } from "./motion";
import { Leaf } from "./Leaf";
import { scrollToId } from "./useLenis";

const Dots = ({ tasks, active }) => (
  <span className="mt-2 flex justify-center gap-1">
    {[...new Set(tasks.map((t) => t[0]))].map((c) => (
      <span key={c} className={`h-1.5 w-1.5 rounded-full ${active ? "ring-1 ring-[#FBF9F4]" : ""}`} style={{ background: CAL_CATS[c].color }} />
    ))}
  </span>
);

const MonthPanel = ({ m }) => (
  <motion.div key={m.name} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
    <p className="font-display text-4xl text-[#1B281E] sm:text-5xl" data-testid="calendar-month-name">{m.name}</p>
    <ul className="mt-6 grid gap-3">
      {m.tasks.map(([c, text]) => (
        <li key={text} data-testid="calendar-task" className="flex items-start gap-4 rounded-2xl border border-[#E2DDD0] bg-[#FBF9F4] px-5 py-4">
          <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: CAL_CATS[c].color }} />
          <span>
            <span className="block text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: CAL_CATS[c].color }}>{CAL_CATS[c].label}</span>
            <span className="mt-0.5 block text-[#1B281E]">{text}</span>
          </span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const SeasonCalendar = () => {
  const [idx, setIdx] = useState(() => new Date().getMonth());
  const m = MONTHS[idx];
  const ask = () => {
    window.dispatchEvent(new CustomEvent("gw:service", { detail: "Saisonale Gartenarbeiten" }));
    scrollToId("kontakt");
  };

  return (
    <section id="gartenjahr" data-testid="calendar-section" className="relative overflow-hidden bg-[#F3EFE6] py-24 md:py-36">
      <Leaf className="right-[5%] top-20 h-14 w-10" color="#6E9A3C" rotate={40} />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Das Gartenjahr" lines={["Was im Garten", "gerade ansteht."]} intro="Ein kleiner Überblick, welche Arbeiten in welchem Monat typischerweise anfallen. Wählen Sie einen Monat aus." />
        <Reveal className="mt-12">
          <div role="tablist" aria-label="Monat wählen" className="grid grid-cols-6 gap-2 md:grid-cols-12">
            {MONTHS.map((mo, i) => (
              <button
                key={mo.short}
                role="tab"
                aria-selected={i === idx}
                data-testid={`calendar-month-${i + 1}`}
                onClick={() => setIdx(i)}
                className={`rounded-2xl py-3 text-sm font-semibold transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 ${
                  i === idx ? "bg-[#1E5631] text-[#FBF9F4]" : "bg-[#FBF9F4] text-[#1B281E] hover:bg-white"
                }`}
              >
                {mo.short}
                <Dots tasks={mo.tasks} active={i === idx} />
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="rounded-[28px] bg-white p-7 md:p-10 lg:col-span-8"><MonthPanel m={m} /></div>
          <Reveal delay={0.1} className="flex flex-col justify-between gap-8 rounded-[28px] bg-[#1E5631] p-7 text-[#FBF9F4] md:p-10 lg:col-span-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#DCEFBE]">Legende</p>
              <ul className="mt-4 grid gap-2">
                {Object.values(CAL_CATS).map((c) => (
                  <li key={c.label} className="flex items-center gap-3"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-[#FBF9F4]/60" style={{ background: c.color }} />{c.label}</li>
                ))}
              </ul>
              <p data-testid="calendar-hedge-note" className="mt-6 flex gap-2 text-sm leading-relaxed text-[#FBF9F4]/85">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                Starke Rückschnitte von Hecken sind laut § 39 BNatSchG vom 1. März bis 30. September nicht erlaubt. Schonende Form- und Pflegeschnitte sind möglich.
              </p>
            </div>
            <button data-testid="calendar-cta" onClick={ask} className="btn-light group w-fit">
              Hilfe im {m.name} anfragen
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[#1E5631] text-[#FBF9F4] transition-transform duration-300 group-hover:rotate-[-45deg]"><ArrowRight className="h-4 w-4" /></span>
            </button>
          </Reveal>
        </div>
        <p className="mt-5 text-sm text-[#4B5B4E]">Allgemeine Orientierung – je nach Wetter und Pflanzen kann sich der richtige Zeitpunkt verschieben.</p>
      </div>
    </section>
  );
};
